import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  ArrowLeft, 
  Sword, 
  Calendar, 
  TrendingUp, 
  Trophy,
  Target,
  Clock,
  Users,
  Medal,
  Crown,
  Heart,
  Flame,
  BarChart3,
  Star
} from "lucide-react";
import KofiModal from "../components/KofiModal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Boss image mapping
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png",
  "Balrog": "https://royals-library.netlify.app/images/monsters/8830000.png"
};

const getBossImage = (bossName) => {
  const baseName = bossName.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

const GuildStatsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guildId = searchParams.get("guild");
  const { user, guilds } = useAuth();
  const [stats, setStats] = useState(null);
  const [guildInfo, setGuildInfo] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuildId, setSelectedGuildId] = useState(guildId || null);
  const [showKofiModal, setShowKofiModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    // If no guild selected and user has guilds, select first one
    if (!selectedGuildId && guilds.length > 0) {
      setSelectedGuildId(guilds[0].id);
    }
  }, [user, guilds, selectedGuildId, navigate]);

  useEffect(() => {
    if (!selectedGuildId) return;

    const fetchGuildData = async () => {
      setLoading(true);
      try {
        const [statsRes, guildRes, membersRes] = await Promise.all([
          axios.get(`${API_URL}/api/guilds/${selectedGuildId}/stats`),
          axios.get(`${API_URL}/api/guilds/${selectedGuildId}`),
          axios.get(`${API_URL}/api/guilds/${selectedGuildId}/members`)
        ]);
        setStats(statsRes.data);
        setGuildInfo(guildRes.data);
        setMembers(membersRes.data);
      } catch (error) {
        toast.error("Failed to load guild statistics");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchGuildData();
  }, [selectedGuildId]);

  if (!user) {
    return null;
  }

  if (guilds.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">You're not in any guild yet</p>
          <Button onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Tracker
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading guild statistics...</p>
        </div>
      </div>
    );
  }

  // Get top 10 bosses by kills
  const topBosses = stats?.kills_by_boss 
    ? Object.entries(stats.kills_by_boss).sort(([, a], [, b]) => b - a).slice(0, 10)
    : [];

  // Get leaderboard sorted by kills
  const leaderboard = stats?.kills_by_member
    ? Object.entries(stats.kills_by_member).sort(([, a], [, b]) => b - a)
    : [];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getMemberAvatar = (username) => {
    const member = members.find(m => m.username === username);
    return member?.avatar;
  };

  const getMedalIcon = (index) => {
    if (index === 0) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (index === 1) return <Medal className="w-5 h-5 text-slate-300" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="guild-stats-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Guild Statistics
              </h1>
              <p className="text-sm text-muted-foreground">{guildInfo?.name || "Select a guild"}</p>
            </div>
          </div>

          {/* Guild Selector */}
          {guilds.length > 1 && (
            <select
              value={selectedGuildId || ""}
              onChange={(e) => setSelectedGuildId(e.target.value)}
              className="bg-background border border-border rounded-md px-3 py-2 text-sm"
              data-testid="guild-selector"
            >
              {guilds.map((guild) => (
                <option key={guild.id} value={guild.id}>
                  {guild.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_kills || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Kills</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.weekly_kills || 0}</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.monthly_kills || 0}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{members.length}</p>
                  <p className="text-xs text-muted-foreground">Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Flame className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.daily_kills || 0}</p>
                  <p className="text-xs text-muted-foreground">Today</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.daily_average || 0}</p>
                  <p className="text-xs text-muted-foreground">Daily Avg</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.best_day?.kills || 0}</p>
                  <p className="text-xs text-muted-foreground">Best Day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold truncate max-w-[80px]" title={stats?.top_hunter?.username}>
                    {stats?.top_hunter?.kills || 0}
                  </p>
                  <p className="text-xs text-muted-foreground truncate" title={stats?.top_hunter?.username}>
                    {stats?.top_hunter?.username ? `#1 ${stats.top_hunter.username}` : "Top Hunter"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Charts */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Kills by Day of Week */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Guild Activity by Day
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.kills_by_day && Object.keys(stats.kills_by_day).length > 0 ? (
                <div className="space-y-3">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                    const count = stats.kills_by_day[day] || 0;
                    const maxKills = Math.max(...Object.values(stats.kills_by_day), 1);
                    const percentage = (count / maxKills) * 100;
                    return (
                      <div key={day} className="flex items-center gap-3">
                        <span className="w-10 text-sm text-muted-foreground">{day}</span>
                        <div className="flex-1 h-6 bg-background/50 rounded overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-primary/60 to-primary rounded transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="w-10 text-sm font-medium text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No activity data yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Weekly Trend */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Weekly Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.kills_by_week && stats.kills_by_week.length > 0 ? (
                <div className="space-y-3">
                  {stats.kills_by_week.map((weekData, index) => {
                    const maxKills = Math.max(...stats.kills_by_week.map(w => w.kills), 1);
                    const percentage = (weekData.kills / maxKills) * 100;
                    const isCurrentWeek = index === stats.kills_by_week.length - 1;
                    return (
                      <div key={weekData.week} className="flex items-center gap-3">
                        <span className={`w-24 text-xs truncate ${isCurrentWeek ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                          {weekData.week}
                        </span>
                        <div className="flex-1 h-6 bg-background/50 rounded overflow-hidden">
                          <div 
                            className={`h-full rounded transition-all duration-500 ${
                              isCurrentWeek 
                                ? 'bg-gradient-to-r from-emerald-500/60 to-emerald-400' 
                                : 'bg-gradient-to-r from-blue-500/40 to-blue-400/60'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className={`w-10 text-sm font-medium text-right ${isCurrentWeek ? 'text-emerald-400' : ''}`}>
                          {weekData.kills}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No weekly data yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend Chart */}
        {stats?.kills_by_month && stats.kills_by_month.length > 0 && (
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Monthly Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-2 h-40">
                {stats.kills_by_month.map((monthData, index) => {
                  const maxKills = Math.max(...stats.kills_by_month.map(m => m.kills), 1);
                  const heightPercentage = (monthData.kills / maxKills) * 100;
                  const isCurrentMonth = index === stats.kills_by_month.length - 1;
                  return (
                    <div key={monthData.month} className="flex flex-col items-center flex-1">
                      <span className={`text-xs mb-1 ${isCurrentMonth ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                        {monthData.kills}
                      </span>
                      <div 
                        className={`w-full rounded-t transition-all duration-500 ${
                          isCurrentMonth 
                            ? 'bg-gradient-to-t from-primary to-primary/60' 
                            : 'bg-gradient-to-t from-muted to-muted/60'
                        }`}
                        style={{ height: `${Math.max(heightPercentage, 5)}%` }}
                      />
                      <span className={`text-xs mt-2 ${isCurrentMonth ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {monthData.month}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Best Day Highlight */}
        {stats?.best_day && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-400">Guild Record!</h3>
                  <p className="text-sm text-muted-foreground">
                    The guild killed <span className="font-bold text-foreground">{stats.best_day.kills} bosses</span> on{" "}
                    <span className="font-medium text-foreground">
                      {new Date(stats.best_day.date).toLocaleDateString("en-US", { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard and Top Bosses */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Member Leaderboard */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Hunter Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No kills recorded yet
                </p>
              ) : (
                <div className="space-y-3">
                  {leaderboard.map(([username, count], index) => {
                    const avatar = getMemberAvatar(username);
                    return (
                      <div 
                        key={username} 
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          index < 3 ? "bg-gradient-to-r from-primary/10 to-transparent" : "bg-background/50"
                        }`}
                        data-testid={`leaderboard-item-${index}`}
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          index === 0 ? "bg-yellow-500/20" :
                          index === 1 ? "bg-slate-400/20" :
                          index === 2 ? "bg-amber-700/20" :
                          "bg-muted"
                        }`}>
                          {getMedalIcon(index) || (
                            <span className="text-xs font-bold text-muted-foreground">
                              {index + 1}
                            </span>
                          )}
                        </span>
                        {avatar ? (
                          <img 
                            src={avatar} 
                            alt={username}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-primary">
                              {username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <span className="flex-1 font-medium truncate">{username}</span>
                        <span className="text-primary font-bold text-lg">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Bosses with Images */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sword className="w-5 h-5" />
                Top Hunted Bosses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topBosses.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No kills recorded yet
                </p>
              ) : (
                <div className="space-y-3">
                  {topBosses.map(([bossName, count], index) => (
                    <div 
                      key={bossName} 
                      className="flex items-center gap-3 p-2 rounded-lg bg-background/50"
                      data-testid={`boss-stat-item-${index}`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        index === 0 ? "bg-yellow-500/20 text-yellow-400" :
                        index === 1 ? "bg-slate-400/20 text-slate-300" :
                        index === 2 ? "bg-amber-700/20 text-amber-600" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {index + 1}
                      </span>
                      <div className="w-10 h-10 bg-background rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                        <img 
                          src={getBossImage(bossName)} 
                          alt={bossName}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <span className="flex-1 font-medium truncate">{bossName}</span>
                      <span className="text-primary font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Guild Kills */}
        <Card className="bg-card/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Guild Kills
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px] pr-4">
              {!stats?.recent_kills || stats.recent_kills.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No kills recorded yet
                </p>
              ) : (
                <div className="space-y-2">
                  {stats.recent_kills.map((kill) => (
                    <div 
                      key={kill.id} 
                      className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/50"
                    >
                      <div className="w-10 h-10 bg-card rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                        <img 
                          src={getBossImage(kill.boss_name)} 
                          alt={kill.boss_name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{kill.boss_name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>CH {kill.channel + 1}</span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(kill.killed_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Ko-fi Support */}
        <Card className="bg-[#FF5E5B]/5 border-[#FF5E5B]/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-[#FF5E5B] fill-[#FF5E5B]" />
                <div>
                  <h3 className="font-semibold text-[#FF5E5B]">Support the Project</h3>
                  <p className="text-xs text-muted-foreground">Help keep the servers running!</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowKofiModal(true)}
                size="sm"
                className="bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white"
              >
                <Heart className="w-4 h-4 mr-1 fill-white" />
                Ko-fi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </div>
  );
};

export default GuildStatsPage;
